import React, { useState, useEffect } from 'react';
import { Plus, Users, Settings, Save, Calendar, MessageSquare, DollarSign, Clock, Package2, Wrench, Users2, BarChart, Settings2, ListChecks, Activity, FileBarChart2, FileText, BarChart3 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { getResourceRoles, updateResourceRole, type ResourceRole, type Permissions } from '../../lib/resourceRoles';
import CreateRoleModal from '../../components/CreateRoleModal';

export default function ResourcePermissions() {
  const { selectedBusiness } = useBusiness();
  const [roles, setRoles] = useState<ResourceRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [originalPermissions, setOriginalPermissions] = useState<Permissions | null>(null);

  // Load roles from database
  const loadRoles = async () => {
    if (!selectedBusiness) return;

    try {
      setIsLoading(true);
      setError(null);
      const rolesData = await getResourceRoles(selectedBusiness.id);
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  };

  // Load roles on component mount
  useEffect(() => {
    loadRoles();
  }, [selectedBusiness]);

  const handleCreateRole = () => {
    setIsCreateModalOpen(true);
  };

  const handleRoleCreated = () => {
    // Reload roles after creating a new one
    loadRoles();
    // Reset selection and permissions state
    setSelectedRoleId(null);
    setPermissions(null);
    setOriginalPermissions(null);
  };

  const handleSelectRole = (roleId: string) => {
    setSelectedRoleId(roleId);
    const role = roles.find(r => r.id === roleId);
    if (role) {
      // Create deep copies to avoid reference issues
      const permissionsCopy = JSON.parse(JSON.stringify(role.permissions));
      setPermissions(permissionsCopy);
      setOriginalPermissions(JSON.parse(JSON.stringify(role.permissions)));
    } else {
      setPermissions(null);
      setOriginalPermissions(null);
    }
  };

  // Check if there are changes to save
  const hasChanges = () => {
    if (!permissions || !originalPermissions) return false;
    return JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
  };

  const handlePermissionChange = (category: keyof Permissions, permission: string, value: boolean) => {
    if (!permissions) return;
    
    setPermissions(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [permission]: value
        }
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId || !permissions) return;

    try {
      setIsSaving(true);
      await updateResourceRole(selectedRoleId, { permissions });
      
      // Update local roles state
      setRoles(prev => prev.map(role => 
        role.id === selectedRoleId 
          ? { ...role, permissions }
          : role
      ));

      // Reset original permissions to current state after successful save
      setOriginalPermissions(permissions);

    } catch (err) {
      console.error('Error saving permissions:', err);
      // You could add a toast notification here
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Resource Permissions</h2>
          <p className="text-gray-600">
            Manage roles and permissions for your team members.
          </p>
        </div>
        
        <button
          onClick={handleCreateRole}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </button>
      </div>

      {/* Main content area with sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Role Library - Left Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Role Library
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {roles.length} {roles.length === 1 ? 'role' : 'roles'}
              </p>
            </div>
            
            <div className="p-2">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Loading roles...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-sm text-red-500 mb-2">{error}</p>
                  <button 
                    onClick={loadRoles}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Try again
                  </button>
                </div>
              ) : roles.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No roles created yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      onClick={() => handleSelectRole(role.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedRoleId === role.id
                          ? 'bg-blue-50 border border-blue-200 text-blue-900'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <div className="font-medium text-sm">{role.name}</div>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {role.description}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area - Right Side */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200">
            {selectedRoleId && permissions ? (
              <div>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex items-center">
                    <Settings className="h-5 w-5 text-gray-400 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">
                      Configure: {roles.find(r => r.id === selectedRoleId)?.name}
                    </h3>
                  </div>
                  <button
                    onClick={handleSavePermissions}
                    disabled={isSaving || !hasChanges()}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      hasChanges() && !isSaving
                        ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving...' : hasChanges() ? 'Save Changes' : 'No Changes'}
                  </button>
                </div>

                {/* Permission Categories */}
                <div className="p-6 space-y-8">
                  {/* Calendar Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Calendar Permissions</h4>
                    </div>
                    
                    {/* Calendar Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Calendar</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.calendar.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.pageAccess}
                                onChange={(e) => handlePermissionChange('calendar', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* Sales */}
                        {permissions.calendar.sales !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Sales</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.sales}
                                onChange={(e) => handlePermissionChange('calendar', 'sales', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* View All Bookings */}
                        {permissions.calendar.viewAllBookings !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">View All Bookings</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.viewAllBookings}
                                onChange={(e) => handlePermissionChange('calendar', 'viewAllBookings', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Booking Info Sub-section */}
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Booking Info</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Booking Information */}
                        {permissions.calendar.bookingInformation !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Booking Information</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.bookingInformation}
                                onChange={(e) => handlePermissionChange('calendar', 'bookingInformation', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* Booking Sales Info */}
                        {permissions.calendar.bookingSalesInfo !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Booking Sales Info</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.bookingSalesInfo}
                                onChange={(e) => handlePermissionChange('calendar', 'bookingSalesInfo', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* Edit Booking Details */}
                        {permissions.calendar.editBookingDetails !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Edit Booking Details</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.editBookingDetails}
                                onChange={(e) => handlePermissionChange('calendar', 'editBookingDetails', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* Booking Materials */}
                        {permissions.calendar.bookingMaterials !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Booking Materials</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.bookingMaterials}
                                onChange={(e) => handlePermissionChange('calendar', 'bookingMaterials', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* Booking Ratebook */}
                        {permissions.calendar.bookingRatebook !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Booking Ratebook</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.bookingRatebook}
                                onChange={(e) => handlePermissionChange('calendar', 'bookingRatebook', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                        
                        {/* Booking Profit Loss */}
                        {permissions.calendar.bookingProfitLoss !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Booking Profit Loss</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.calendar.bookingProfitLoss}
                                onChange={(e) => handlePermissionChange('calendar', 'bookingProfitLoss', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <MessageSquare className="h-5 w-5 text-green-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Messages Permissions</h4>
                    </div>
                    
                    {/* Messages Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Messages</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.messages?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.messages.pageAccess}
                                onChange={(e) => handlePermissionChange('messages', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* CRM Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Users className="h-5 w-5 text-purple-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">CRM Permissions</h4>
                    </div>
                    
                    {/* CRM Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">CRM</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.crm?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.crm.pageAccess}
                                onChange={(e) => handlePermissionChange('crm', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Payroll Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <DollarSign className="h-5 w-5 text-orange-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Payroll Permissions</h4>
                    </div>
                    
                    {/* Payroll Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Payroll</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.payroll?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.payroll.pageAccess}
                                onChange={(e) => handlePermissionChange('payroll', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Timeclock Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Clock className="h-5 w-5 text-red-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Timeclock Permissions</h4>
                    </div>
                    
                    {/* Timeclock Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Timeclock</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.timeclock?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.timeclock.pageAccess}
                                onChange={(e) => handlePermissionChange('timeclock', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Supplies Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Package2 className="h-5 w-5 text-green-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Supplies Permissions</h4>
                    </div>
                    
                    {/* Supplies Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Supplies</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.supplies?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.supplies.pageAccess}
                                onChange={(e) => handlePermissionChange('supplies', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Services Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Wrench className="h-5 w-5 text-blue-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Services Permissions</h4>
                    </div>
                    
                    {/* Services Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Services</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.services?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.services.pageAccess}
                                onChange={(e) => handlePermissionChange('services', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resources Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Users2 className="h-5 w-5 text-purple-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Resources Permissions</h4>
                    </div>
                    
                    {/* Resources Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Resources</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.resources?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.resources.pageAccess}
                                onChange={(e) => handlePermissionChange('resources', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sales Reports Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <BarChart className="h-5 w-5 text-red-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Sales Reports Permissions</h4>
                    </div>
                    
                    {/* Sales Reports Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Sales Reports</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.salesReports?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.salesReports.pageAccess}
                                onChange={(e) => handlePermissionChange('salesReports', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Team Reports Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <BarChart className="h-5 w-5 text-indigo-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Team Reports Permissions</h4>
                    </div>
                    
                    {/* Team Reports Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Team Reports</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.teamReports?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.teamReports.pageAccess}
                                onChange={(e) => handlePermissionChange('teamReports', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Supplies Usage Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <BarChart className="h-5 w-5 text-yellow-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Supplies Usage Permissions</h4>
                    </div>
                    
                    {/* Supplies Usage Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Supplies Usage</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.suppliesUsage?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.suppliesUsage.pageAccess}
                                onChange={(e) => handlePermissionChange('suppliesUsage', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Settings Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Settings2 className="h-5 w-5 text-gray-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Settings Permissions</h4>
                    </div>
                    
                    {/* Settings Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Settings</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.settings?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.settings.pageAccess}
                                onChange={(e) => handlePermissionChange('settings', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after-border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tasks Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <ListChecks className="h-5 w-5 text-teal-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Tasks Permissions</h4>
                    </div>
                    
                    {/* Tasks Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Tasks</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.tasks?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.tasks.pageAccess}
                                onChange={(e) => handlePermissionChange('tasks', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bookings Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <FileBarChart2 className="h-5 w-5 text-emerald-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Bookings Permissions</h4>
                    </div>
                    
                    {/* Bookings Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Bookings</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.bookings?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.bookings.pageAccess}
                                onChange={(e) => handlePermissionChange('bookings', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Proposals Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <FileText className="h-5 w-5 text-amber-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Proposals Permissions</h4>
                    </div>
                    
                    {/* Proposals Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Proposals</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.proposals?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.proposals.pageAccess}
                                onChange={(e) => handlePermissionChange('proposals', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Customers Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Users className="h-5 w-5 text-pink-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Customers Permissions</h4>
                    </div>
                    
                    {/* Customers Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Customers</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.customers?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.customers.pageAccess}
                                onChange={(e) => handlePermissionChange('customers', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* KPI Dashboard Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <BarChart3 className="h-5 w-5 text-violet-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">KPI Dashboard Permissions</h4>
                    </div>
                    
                    {/* KPI Dashboard Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">KPI Dashboard</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.kpiDashboard?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.kpiDashboard.pageAccess}
                                onChange={(e) => handlePermissionChange('kpiDashboard', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Automations Permissions */}
                  <div>
                    <div className="flex items-center mb-6">
                      <Activity className="h-5 w-5 text-cyan-500 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Automations Permissions</h4>
                    </div>
                    
                    {/* Automations Sub-section */}
                    <div className="mb-6">
                      <h5 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">Automations</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Page Access */}
                        {permissions.automations?.pageAccess !== undefined && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Page Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions.automations.pageAccess}
                                onChange={(e) => handlePermissionChange('automations', 'pageAccess', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select a role to configure
                </h3>
                <p className="text-gray-500">
                  Choose a role from the library to view and edit its permissions.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      <CreateRoleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onRoleCreated={handleRoleCreated}
        businessId={selectedBusiness?.id || ''}
      />
    </div>
  );
} 